import { getSpeakerNameAndTitle } from '@/player-config/src/utils/speakerUtils';
import { Scene } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { ORIGINAL_VIDEO_ID } from '@/player-config/src/constants/screenshare';
import { sharedVideoBarsColors } from '@/player-config/src/constants/speakerColors';
import { SpeakerWithDetails } from '@/player-config/src/types/speaker-types';
import { VideoFeed, VideoFeedType } from '@/player-config/src/types/video-feed-types';

export type SpeakerTimelineTrack = Pick<SpeakerWithDetails, 'bgColor' | 'lightBgColor' | 'color'> & {
  id: string;
  name: string;
  toggleKey: string;
  selected: boolean;
};

export const SCREENSHARE_TRACK_NAME = 'Shared Screen';
export const SHARED_VIDEO_TRACK_NAME = 'Shared Video';

const buildTrackName = ({
  videoId,
  feed,
  speakerDetails
}: {
  videoId: string;
  feed?: VideoFeed;
  speakerDetails: Record<string, SpeakerWithDetails>;
}): string => {
  const speaker = (feed?.speakerKey ? speakerDetails[feed.speakerKey] : undefined) ?? feed?.speakerDetails;

  if (feed?.type === VideoFeedType.USER_FEED && speaker) {
    return getSpeakerNameAndTitle(speaker).name;
  }

  if (videoId === ORIGINAL_VIDEO_ID) {
    return 'Original track';
  }

  if (feed?.type === VideoFeedType.SHARED_VIDEO) {
    return SHARED_VIDEO_TRACK_NAME;
  }

  return SCREENSHARE_TRACK_NAME;
};

export const buildSceneTracks = ({
  scene,
  videoFeeds,
  speakerDetails
}: {
  scene?: Scene;
  videoFeeds: VideoFeed[];
  speakerDetails: Record<string, SpeakerWithDetails>;
}): SpeakerTimelineTrack[] => {
  const allVideoIds: string[] = scene?.details.all_video_ids ?? [];
  const visibleVideoIds: string[] = scene?.details.visible_video_ids ?? [];
  const videoFeedsById = new Map(videoFeeds.map(feed => [feed.id, feed]));

  const tracks = allVideoIds.map(videoId => {
    const feed = videoFeedsById.get(videoId);
    const speaker = (feed?.speakerKey ? speakerDetails[feed.speakerKey] : undefined) ?? feed?.speakerDetails;
    const name = buildTrackName({ videoId, feed, speakerDetails });
    const toggleKey = feed?.type === VideoFeedType.USER_FEED && feed.speakerKey ? feed.speakerKey : name;

    return {
      id: videoId,
      name,
      toggleKey,
      selected: visibleVideoIds.includes(videoId),
      bgColor: speaker?.bgColor || 'bg-slate-100',
      lightBgColor: speaker?.lightBgColor || 'bg-slate-100',
      color: speaker?.color || 'text-slate-700'
    };
  }) as SpeakerTimelineTrack[];

  if (!scene) {
    return tracks;
  }

  const screenshareTracks: SpeakerTimelineTrack[] = [];
  const sharedVideoTracks: SpeakerTimelineTrack[] = [];
  const userVideoTracks: SpeakerTimelineTrack[] = [];

  tracks.forEach(track => {
    const feed = videoFeedsById.get(track.id);
    if (feed?.type === VideoFeedType.SCREENSHARE) {
      screenshareTracks.push(track);
    } else if (feed?.type === VideoFeedType.SHARED_VIDEO) {
      sharedVideoTracks.push(track);
    } else {
      userVideoTracks.push(track);
    }
  });

  const finalTracks: SpeakerTimelineTrack[] = [...userVideoTracks];

  if (screenshareTracks.length > 0) {
    finalTracks.push({
      id: screenshareTracks[0].id,
      name: SCREENSHARE_TRACK_NAME,
      toggleKey: SCREENSHARE_TRACK_NAME,
      selected: screenshareTracks.every(track => track.selected),
      bgColor: 'bg-slate-100',
      lightBgColor: 'bg-slate-100',
      color: 'text-slate-700'
    });
  }

  if (sharedVideoTracks.length > 0) {
    finalTracks.push({
      id: sharedVideoTracks[0].id,
      name: SHARED_VIDEO_TRACK_NAME,
      toggleKey: SHARED_VIDEO_TRACK_NAME,
      selected: sharedVideoTracks.every(track => track.selected),
      bgColor: sharedVideoBarsColors.bgColor,
      lightBgColor: sharedVideoBarsColors.lightBgColor,
      color: sharedVideoBarsColors.textColor
    });
  }

  return finalTracks;
};
